import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Printer } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/Popover.js';

export type ExportDocType = 'prd' | 'tdd' | 'tasks' | 'timeline';

/**
 * "Export" for one document: Markdown is generated server-side and downloaded immediately (see
 * docs/PLAN.md §8); PDF is out of scope server-side — it opens the print-optimised route in a new
 * tab, and the user prints to PDF from there via the browser's own print dialog.
 */
export function ExportMenu({
  projectId,
  docType,
  disabled,
  className,
}: {
  projectId: string;
  docType: ExportDocType;
  disabled?: boolean;
  className?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function downloadMarkdown() {
    setBusy(true);
    setError(null);
    try {
      const { downloadUrl } = await api.post<{ downloadUrl: string }>(
        `/projects/${projectId}/exports`,
        {
          docType,
          format: 'markdown',
        },
      );
      window.open(downloadUrl, '_blank', 'noopener');
    } catch {
      setError('Could not generate the export.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" disabled={disabled} className={className}>
          Export
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-52 flex-col gap-0.5 p-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => void downloadMarkdown()}
          className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-text hover:bg-surface-2 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Markdown (.md)
        </button>
        <Link
          to={`/projects/${projectId}/print/${docType}`}
          target="_blank"
          rel="noopener"
          className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-text hover:bg-surface-2"
        >
          <Printer className="h-3.5 w-3.5" /> Print / PDF
        </Link>
        {error && <p className="px-2.5 pt-1 text-xs text-danger">{error}</p>}
      </PopoverContent>
    </Popover>
  );
}
